// Tokenizer for `@if` condition expressions with logical/comparison operators.
// Supports `&&`, `||`, `===`, `!==`, `>=`, `<=`, `>`, `<`, and `var(...)`.

export type ConditionToken = { type: 'lparen'; value: '(' } | { type: 'rparen'; value: ')' } | { type: 'operator'; value: '&&' | '||' | '===' | '!==' | '>=' | '<=' | '>' | '<' } | { type: 'value'; value: string }
type ConditionOperator = Extract<ConditionToken, { type: 'operator' }>['value']

// Convert raw condition string into parser tokens.
export function tokenizeConditionExpression(source: string): ConditionToken[] | null {
  const tokens: ConditionToken[] = []
  let index = 0

  // Scan one token at a time until full expression is tokenized.
  while (index < source.length) {
    const char = source[index]
    // Skip whitespace between tokens.
    if (isWhitespace(char)) {
      index += 1
      continue
    } else {
      // Continue token parsing for non-whitespace chars.
    }

    // Emit parenthesis tokens directly.
    if (char === '(') {
      tokens.push({ type: 'lparen', value: '(' })
      index += 1
      continue
    } else if (char === ')') {
      tokens.push({ type: 'rparen', value: ')' })
      index += 1
      continue
    } else {
      // Continue operator/value parsing.
    }

    const operator = readOperatorAt(source, index)
    // Emit operator token when matched.
    if (operator) {
      tokens.push({ type: 'operator', value: operator })
      index += operator.length
      continue
    } else {
      // Continue value-token parsing when no operator matches.
    }

    const valueToken = readValueTokenAt(source, index)
    // Reject expressions with malformed value tokens.
    if (!valueToken) {
      return null
    } else {
      // Emit parsed value token and continue scanning.
      tokens.push({ type: 'value', value: valueToken.value })
      index = valueToken.endIndex
    }
  }

  // Return tokenized condition expression.
  return tokens
}

// Read longest supported operator token at scanner index.
function readOperatorAt(source: string, index: number): ConditionOperator | null {
  const current = source[index]
  const next = source[index + 1] ?? ''
  const afterNext = source[index + 2] ?? ''
  // Match `===` operator token.
  if (current === '=' && next === '=' && afterNext === '=') {
    return '==='
  } else if (current === '!' && next === '=' && afterNext === '=') {
    // Match `!==` operator token.
    return '!=='
  } else if (current === '>' && next === '=') {
    // Match `>=` operator token.
    return '>='
  } else if (current === '<' && next === '=') {
    // Match `<=` operator token.
    return '<='
  } else if (current === '&' && next === '&') {
    // Match `&&` operator token.
    return '&&'
  } else if (current === '|' && next === '|') {
    // Match `||` operator token.
    return '||'
  } else if (current === '>') {
    // Match `>` operator token.
    return '>'
  } else if (current === '<') {
    // Match `<` operator token.
    return '<'
  } else {
    // Return null when no operator token matches.
    return null
  }
}

// Read one value token (quoted/string/identifier/var-call) from index.
function readValueTokenAt(source: string, startIndex: number): { value: string; endIndex: number } | null {
  const char = source[startIndex]
  // Parse quoted literal tokens.
  if (char === "'" || char === '"') {
    return readQuotedToken(source, startIndex)
  } else if (source.slice(startIndex, startIndex + 4) === 'var(') {
    // Parse `var(...)` tokens with balanced parentheses.
    return readVarCallToken(source, startIndex)
  } else {
    // Parse plain value tokens.
    return readPlainToken(source, startIndex)
  }
}

// Read quoted string token and include quote chars in token value.
function readQuotedToken(source: string, startIndex: number): { value: string; endIndex: number } | null {
  const quote = source[startIndex]
  let index = startIndex + 1

  // Scan until matching non-escaped quote is found.
  while (index < source.length) {
    const char = source[index]
    const previous = index > startIndex ? source[index - 1] : ''
    if (char === quote && previous !== '\\') {
      return {
        value: source.slice(startIndex, index + 1),
        endIndex: index + 1,
      }
    } else {
      // Continue quoted-token scanning.
    }
    index += 1
  }

  // Return null for unterminated quoted tokens.
  return null
}

// Read `var(...)` token with balanced parentheses.
function readVarCallToken(source: string, startIndex: number): { value: string; endIndex: number } | null {
  let depth = 0
  for (let index = startIndex; index < source.length; index += 1) {
    const char = source[index]
    if (char === '(') {
      depth += 1
    } else if (char === ')') {
      depth -= 1
      if (depth === 0) {
        return {
          value: source.slice(startIndex, index + 1),
          endIndex: index + 1,
        }
      }
    } else {
      // Continue var-call token scan.
    }
  }

  // Return null for unbalanced var-call tokens.
  return null
}

// Read plain token until whitespace, parenthesis, or operator boundary.
function readPlainToken(source: string, startIndex: number): { value: string; endIndex: number } | null {
  let index = startIndex
  // Consume chars until token delimiter is reached.
  while (index < source.length) {
    const char = source[index]
    const operatorStart = char === '=' || char === '!' || char === '>' || char === '<' || char === '&' || char === '|'
    if (isWhitespace(char) || char === '(' || char === ')' || (operatorStart && readOperatorAt(source, index))) {
      break
    } else {
      index += 1
    }
  }

  const value = source.slice(startIndex, index).trim()
  // Return null for empty plain tokens.
  if (!value) {
    return null
  } else {
    // Return parsed plain token with new cursor index.
    return { value, endIndex: index }
  }
}

// Check if character is whitespace.
function isWhitespace(char: string): boolean {
  return char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f'
}
